import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { getChangeListData } from '../../services/apiService';
import { useAssessment } from '../../components/HardwareChangeVerification/AssessmentContext';



const AssessmentDetail = () => {
  const { tab, item } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [changes, setChanges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [noChangeSelected, setNoChangeSelected] = useState(false);
  const subType = location.state?.subType || null;
  const effectiveProductType = subType || tab;



  const {
    getTabData,
    setReferenceModel,
    setCompletedItem,
    toggleChangeSelection
  } = useAssessment();



  const tabData = getTabData(tab);
  const referenceModel = tabData.referenceModels[item] || '';



  // Check if any changes are selected
  const hasSelectedChanges = changes.some(change => change.applied);



  // Function to filter out header rows
  const filterHeaderRows = (data) => {
    return data.filter(row => {
      // Skip header rows
      if (row.id && (
        row.id.includes('变更编号') || 
        row.id.includes('Change item ID') ||
        row.id === 'Change Item ID' ||
        row.id === '变更编号' ||
        row.detail?.includes('Change Content Detail') ||
        row.detail?.includes('变更明细')
      )) {
        console.log(`Filtering out header row:`, row.id);
        return false;
      }
      
      // Skip empty or invalid rows
      return row.id && row.detail && row.id.trim() !== '' && row.detail.trim() !== '';
    });
  };



  // IMPORTANT: Keep track of context selectedChanges to sync with local state
  useEffect(() => {
    const contextSelectedChanges = tabData.selectedChanges[item] || [];
    console.log(`Context selected changes for ${item}:`, contextSelectedChanges);
    
    // Update local state to match context when context changes
    if (changes.length > 0) {
      const updatedChanges = changes.map(change => {
        const isInContext = contextSelectedChanges.some(selected => selected.id === change.id);
        return {
          ...change,
          applied: isInContext
        };
      });
      
      setChanges(updatedChanges);
    }
  }, [tabData.selectedChanges[item], item]);



 useEffect(() => {
  const loadData = async () => {
    try {
      setLoading(true);
      
      console.log('Loading data for:', { tab, item });

      let sectionData = [];

      // Check if we have saved complete data first
      if (tabData.completeChangeListData && tabData.completeChangeListData[item]) {
        console.log(`Using saved complete data for ${item}`);
        sectionData = tabData.completeChangeListData[item];
      } else if (tabData.changeListData && tabData.changeListData[item]) {
        console.log(`Using loaded change list data for ${item}`);
        sectionData = tabData.changeListData[item];
      } else if (tabData.changeListId) {
        // FIXED: Proper null handling for changeListId
        console.log(`Loading fresh data from API for ${item}`);
        
        // Safely extract the changeListId string
        let changeListId;
        if (typeof tabData.changeListId === 'string') {
          changeListId = tabData.changeListId;
        } else if (tabData.changeListId && typeof tabData.changeListId === 'object' && tabData.changeListId._id) {
          changeListId = String(tabData.changeListId._id);
        } else {
          throw new Error('Invalid changeListId format');
        }
        
        console.log('Using change list ID:', changeListId);
        
        const response = await getChangeListData(changeListId);
        sectionData = response[item] || [];
      } else {
        // No data available at all
        console.warn(`No change list data available for ${tab}/${item}`);
        setError('No assessment data available. Please import a change list first.');
        setLoading(false);
        return;
      }

      // Filter out header rows
      const filteredSectionData = filterHeaderRows(sectionData);
      console.log(`Section data for ${item} (after filtering):`, filteredSectionData.length, 'items');

      // Map the data with proper applied status
      const contextSelectedChanges = tabData.selectedChanges[item] || [];
      const selectedIds = new Set(contextSelectedChanges.map(change => change.id));

      const dataWithStatus = filteredSectionData.map(row => {
        const isSelectedInContext = selectedIds.has(row.id);
        
        // Check if detail contains "无变更(No Change)" or "无变更"
        const isNoChange = row.detail && (
          row.detail.includes('无变更(No Change)') || 
          row.detail.includes('无变更') ||
          row.detail.toLowerCase().includes('no change')
        );
        
        return {
          id: row.id,
          detail: row.detail,
          applied: isSelectedInContext,
          isNoChange: isNoChange // Flag to identify "No Change" rows
        };
      });

      // Check if any "No Change" row is selected
      const hasNoChangeSelected = dataWithStatus.some(row => row.isNoChange && row.applied);
      setNoChangeSelected(hasNoChangeSelected);

      console.log(`Processed data for ${item}:`, dataWithStatus.length, 'valid items');
      setChanges(dataWithStatus);
      setError(null);
    } catch (err) {
      console.error('Error loading assessment data:', err);
      setError(`Failed to load assessment data: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  loadData();
}, [tabData, item, tab]);




  const handleToggleApplied = (index) => {
    const change = changes[index];
  
  // If "No Change" is selected and this is NOT the "No Change" row, prevent the action
  if (noChangeSelected && !change.isNoChange) {
    console.log(`Cannot toggle ${change.id}: "No Change" is selected`);
    return; // Exit early - prevents any state change
  }
  
    const newAppliedState = !change.applied;
    
    console.log(`Toggling ${change.id}: ${change.applied} -> ${newAppliedState}`);
    
    // Update local state immediately for UI responsiveness
    const updatedChanges = [...changes];
    updatedChanges[index] = {
      ...updatedChanges[index],
      applied: newAppliedState
    };
  
  // If this is a "No Change" row being checked, uncheck all other rows
  if (change.isNoChange && newAppliedState) {
    setNoChangeSelected(true);
    // Uncheck all other rows
    updatedChanges.forEach((row, idx) => {
      if (idx !== index && row.applied) {
        updatedChanges[idx] = { ...row, applied: false };
        // Remove from context
        toggleChangeSelection(tab, item, row.id, row.detail, false);
      }
    });
  } else if (change.isNoChange && !newAppliedState) {
    // "No Change" is being unchecked, enable other checkboxes
    setNoChangeSelected(false);
  }
  
    setChanges(updatedChanges);



    // Update context state
    toggleChangeSelection(
      tab,
      item,
      change.id,
      change.detail,
      newAppliedState
    );
  };

  const handleReferenceModelChange = (e) => {
    const value = e.target.value;
    setReferenceModel(tab, item, value);
  };

  const handleBackClick = () => {
    navigate('/hardware-change', {
      state: {
        activeTab: tab,
        selectedChanges: tabData.selectedChanges,
        referenceModels: tabData.referenceModels,
        changeListId: tabData.changeListId,
        completedItems: Array.from(tabData.completedItems),
        subType: subType
      }
    });
  };



  const handleDone = () => {
    // **REMOVED**: No validation needed - reference model is now completely optional
    setCompletedItem(tab, item);



    navigate('/hardware-change', {
      state: {
        activeTab: tab,
        selectedChanges: tabData.selectedChanges,
        referenceModels: tabData.referenceModels,
        changeListId: tabData.changeListId,
        completedItems: Array.from(new Set([...tabData.completedItems, item])),
        subType: subType
      }
    });
  };



  if (loading) {
    return (
      <div className="h-screen bg-black text-white flex items-center justify-center overflow-hidden">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-400">Loading assessment data for {effectiveProductType} / {item}...</p>
        </div>
      </div>
    );
  }



  if (error) {
    return (
      <div className="h-screen bg-black text-white flex items-center justify-center overflow-hidden">
        <div className="text-center max-w-md">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h2 className="text-xl font-semibold mb-2 text-red-400">Error Loading Data</h2>
          <p className="text-gray-400 mb-4">{error}</p>
          <button
            onClick={handleBackClick}
            className="px-6 py-3 bg-gray-600 rounded-lg text-white hover:bg-gray-700 transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }



  return (
    <div className="h-screen bg-black text-white flex flex-col overflow-hidden">
      {/* Sticky Header - Fixed Height */}
      <div className="flex-shrink-0 bg-black border-b border-gray-800 px-4 sm:px-6 py-4">
        <div className="flex items-center mb-4">
          <button
            onClick={handleBackClick}
            className="p-2 -ml-2 hover:bg-gray-800 rounded-lg transition-colors duration-200 mr-2"
            aria-label="Go back"
          >
            <ArrowLeft className="w-5 h-5 text-white" />
          </button>
          <h1 className="text-base sm:text-lg font-medium text-gray-300">
            {`HW Change Assessment / ${effectiveProductType} / ${item}`}          
        </h1>
        </div>
        
        <div className="flex flex-col gap-4">
          <div>
            <h2 className="text-xl sm:text-2xl font-semibold text-white mb-1">
              {`${item} Changed Item`}
            </h2>
            <p className="text-gray-400 text-sm">Select the items that have been changed.</p>
          </div>
          
          <div className="flex flex-col gap-2 max-w-md">
            <label className="text-white text-sm font-medium">
              {`${item} Reference Model:`}
            </label>
            <input
              type="text"
              value={referenceModel}
              onChange={handleReferenceModelChange}
              className="bg-gray-800 text-white border border-gray-600 rounded-lg px-3 py-2 text-sm 
                       focus:ring-1 focus:outline-none focus:border-blue-500 focus:ring-blue-500/20
                       transition-colors duration-200 w-full"
              placeholder="Enter reference model"
            />
            <p className="text-gray-500 text-xs">
              Enter the reference model for comparison if needed
            </p>
          </div>
        </div>
      </div>



      {/* Scrollable Content Area - Grows to fill available space */}
      <div className="flex-1 overflow-hidden" style={{ scrollbarwidth: 'thin', scrollbarColor: 'gray'}} >
        {changes.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full px-4 sm:px-6">
            <div className="text-gray-500 text-6xl mb-4">📋</div>
            <h3 className="text-xl font-semibold mb-2 text-gray-400">No Data Available</h3>
            <p className="text-gray-500">No assessment items found for {item} section.</p>
          </div>
        ) : (
          <>
            {/* Mobile View */}
            <div className="block sm:hidden h-full overflow-y-auto px-4 py-4">
              <div className="space-y-3 pb-24">
                {changes.map((row, index) => {
      // Only block interaction via cursor, don't disable checkbox
      const isBlocked = noChangeSelected && !row.isNoChange;
      
      return (
                  <div
                    key={index}
                    className="bg-gray-900/50 rounded-lg border border-gray-800 p-4 
                             hover:bg-gray-900/70 transition-colors duration-200"
                  >
                    <div className="flex items-start gap-3">
                      <label className={`relative flex items-center min-w-[20px] ${isBlocked ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
                        <input
                          type="checkbox"
                          checked={row.applied}
                          onChange={() => handleToggleApplied(index)}
                          className="sr-only peer"
                        />
                        <div className={`w-5 h-5 bg-gray-800 border-2 rounded-md 
                           transition-all duration-200 
                           flex items-center justify-center
                           border-gray-600 peer-checked:bg-blue-600 peer-checked:border-blue-600 
                           peer-focus:ring-2 peer-focus:ring-blue-500/20 
                           ${isBlocked ? '' : 'hover:border-gray-500'}`}>
                                     
                          {row.applied && (
                            <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" 
                                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" 
                                    clipRule="evenodd" />
                            </svg>
                          )}
                        </div>
                      </label>



                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-blue-400 text-sm mb-1">
                          {row.id}
                        </div>
                        <div className="text-white text-sm leading-relaxed break-words">
                          {row.detail}
                        </div>
                      </div>
                    </div>
                  </div>
                );
    })}
              </div>
            </div>



            {/* Desktop View */}
            <div className="hidden sm:block h-full px-4 sm:px-6 py-6">
              <div className="h-full flex flex-col">
                <div className="border border-gray-800 rounded-lg overflow-hidden flex-1 flex flex-col min-h-0">
                  {/* Fixed Header */}
                <div className="flex-shrink-0 bg-gray-900">
  <table className="w-full text-left border-collapse">
    <thead>
      <tr>
        {/* Left: Change Item ID */}
        <th className="p-4 text-sm font-semibold border-b border-gray-700 text-left text-white">
          变更编号 / Change Item ID
        </th>

        {/* Center: Change Content Detail */}
        <th className="p-4 text-sm font-semibold border-b border-gray-700 text-center text-white">
          变更明细 / Change Content Detail
        </th>

        {/* Right: Change Applied? */}
        <th className="p-4 text-sm font-semibold border-b border-gray-700 text-right text-white pr-6">
          是否变更？ / Change applied？
        </th>
      </tr>
    </thead>
  </table>
</div>

                  {/* Scrollable Body */}
                  {/* <div className="flex-1 overflow-y-auto min-h-0">
                    <table className="w-full text-left border-collapse">
                      <colgroup>
                        <col className="w-40" />
                        <col />
                        <col className="w-32" />
                      </colgroup>
                      <tbody>
                        {changes.map((row, index) => (
                          <tr key={index} 
                              className="hover:bg-gray-900/50 transition-colors duration-200 
                                      border-b border-gray-800 last:border-b-0">
                            <td className="p-4 font-medium text-blue-400 text-sm align-top">
                              {row.id}
                            </td>
                            <td className="p-4 text-white text-sm leading-relaxed align-top">
                              {row.detail}
                            </td>
                            <td className="p-4 align-top">
                              <div className="flex justify-center">
                                <label className="relative inline-flex items-center cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={row.applied}
                                    onChange={() => handleToggleApplied(index)}
                                    className="sr-only peer"
                                  />
                                  <div className="w-5 h-5 bg-gray-800 border-2 border-gray-600 rounded-md 
                                               peer-checked:bg-blue-600 peer-checked:border-blue-600 
                                               peer-focus:ring-2 peer-focus:ring-blue-500/20
                                               hover:border-gray-500 transition-all duration-200 
                                               flex items-center justify-center">
                                    {row.applied && (
                                      <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" 
                                              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" 
                                              clipRule="evenodd" />
                                      </svg>
                                    )}
                                  </div>
                                </label>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div> */}
<div className="flex-1 overflow-y-auto min-h-0">
  <table className="w-full text-left border-collapse">
    <colgroup>
      <col className="w-40" />   {/* Change Item ID */}
      <col />                    {/* Change Content Detail – takes remaining space */}
      <col className="w-32" />   {/* Checkbox */}
    </colgroup>

    <tbody>
      {changes.map((row, index) => {
        // Only block interaction via cursor, don't disable checkbox
        const isBlocked = noChangeSelected && !row.isNoChange;
        
        return (
        <tr
          key={index}
          className="hover:bg-gray-900/50 transition-colors duration-200 
                     border-b border-gray-800 last:border-b-0"
        >
          {/* 1. LEFT – Change Item ID */}
          <td className="p-4 font-medium text-blue-400 text-sm align-top">
            {row.id}
          </td>

          {/* 2. CENTER – Change Content Detail (perfectly centered) */}
<td className="p-4 text-white text-sm leading-relaxed align-top">
  <div className="flex justify-center">
    <div className="text-left max-w-full">
      {row.detail}
    </div>
  </div>
</td>

          {/* 3. CENTER – Checkbox (centered inside the cell) */}
          <td className="p-4 align-top">
            <div className="flex justify-center">
              <label className={`relative inline-flex items-center ${isBlocked ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
                <input
                  type="checkbox"
                  checked={row.applied}
                  onChange={() => handleToggleApplied(index)}
                  className="sr-only peer"
                />
                <div
                  className={`w-5 h-5 rounded-md border-2 
                             transition-all duration-200 
                             flex items-center justify-center
                             bg-gray-800 border-gray-600 
                             peer-checked:bg-blue-600 peer-checked:border-blue-600 
                             peer-focus:ring-2 peer-focus:ring-blue-500/20 
                               ${isBlocked ? '' : 'hover:border-gray-500'}`}
                >
                  {row.applied && (
                    <svg
                      className="w-3 h-3 text-white"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  )}
                </div>
              </label>
            </div>
          </td>
        </tr>
      );
      })}
    </tbody>
  </table>
</div>
                  
                </div>
              </div>
            </div>
          </>
        )}
      </div>



      {/* Fixed Bottom Button */}
      <div className="flex-shrink-0 bg-black/95 backdrop-blur-sm border-t border-gray-800 p-4 sm:p-6">
        <div className="flex justify-end max-w-8xl mx-auto pr-4 sm:pr-6 lg:pr-8">
          <button
            onClick={handleDone}
            className="px-6 py-3 bg-blue-600 rounded-lg text-white font-medium
                     hover:bg-blue-700 focus:ring-2 focus:ring-blue-500/20 focus:outline-none
                     transition-all duration-200 transform hover:scale-105 active:scale-95
                     min-w-[120px] text-sm sm:text-base"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};



export default AssessmentDetail;
